/**
 * Waits for an element matching the given CSS selector to appear in the DOM.
 * 
 * Uses MutationObserver to efficiently watch for DOM changes and resolves immediately
 * if the element already exists. Includes a configurable timeout to prevent indefinite waiting.
 * 
 * @param {string} selector - CSS selector string to match the desired element (e.g., '.my-class', '#my-id')
 * @param {number} [timeout=5000] - Maximum time to wait in milliseconds before rejecting (default: 5000ms)
 * @returns {Promise<Element>} Promise that resolves with the found element or rejects on timeout
 * 
 **/
export function waitForElement(selector: string, timeout = 5000) {
  return new Promise((resolve) => {
    // Check if element already exists
    const element = document.querySelector(selector);
    if (element) {
      return resolve(element);
    }

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(element);
      }
    });

    // Observe the document root for changes
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    // Set timeout to resolve with null if element never appears
    const timer = setTimeout(() => {
      observer.disconnect();
      console.warn(`Element "${selector}" not found within ${timeout}ms`);
      resolve(null);
    }, timeout);
  });
}